import { DataViewGallery, type DataViewGalleryProps } from "@/features/common/components/dataview/DataViewGallery";
import { DataViewList, type DataViewListProps } from "@/features/common/components/dataview/DataViewList";
import type { DataViewBaseProps, DataViewDataProps } from "@/features/common/components/dataview/DataViewTypes";
import { screenSizeIsConsideredMobile, useScreenSize } from "@/lib/common/ScreenSizeContext";
import type { ViewType } from "@/lib/common/ViewType";
import { usePreferredViewType } from "@/lib/state/usePreferredViewType";
import React, { useMemo } from "react";

export type { DataViewComponentTypeProps, DataViewDataProps, DataViewBaseProps } from "@/features/common/components/dataview/DataViewTypes";
export type { DataViewListProps } from "@/features/common/components/dataview/DataViewList";
export type { DataViewGalleryProps } from "@/features/common/components/dataview/DataViewGallery";
export { GalleryCard, type GalleryCardProps } from "@/features/common/components/dataview/DataViewGallery";

export type DataViewViewProps<T, U = undefined> = DataViewListProps<T, U> | DataViewGalleryProps<T, U> | (DataViewListProps<T, U> & DataViewGalleryProps<T, U>);
export type DataViewProps<T, U = undefined> = DataViewBaseProps<T, U> & DataViewDataProps<T> & DataViewViewProps<T, U>;

export const DataView = <T, U = undefined>(props: DataViewProps<T, U>): React.JSX.Element => {
	const supportsList = "listComponent" in props;
	const supportsGallery = "galleryComponent" in props;

	const screenSize = useScreenSize();
	const preferredViewType = usePreferredViewType();

	const computedViewType = useMemo<ViewType>(() => {
		if (supportsList && supportsGallery) {
			if (props.onlyUseGalleryForMobile && !screenSizeIsConsideredMobile(screenSize)) {
				return "List";
			}

			return preferredViewType;
		}

		return supportsList ? "List" : "Gallery";
	}, [supportsList, supportsGallery, preferredViewType, screenSize, props.onlyUseGalleryForMobile]);

	const items = "items" in props ? props.items : undefined;
	const forQuery = "forQuery" in props ? props.forQuery : undefined;

	const isInEmptyState = useMemo(() => {
		if (items && items.length > 0) {
			return false;
		}

		if (!forQuery) {
			return true;
		}

		const totalResults = forQuery.data?.pages.reduce((sum, page) => sum + page.results.length, 0) ?? 0;
		return totalResults === 0 && forQuery.isSuccess;
	}, [items, forQuery]);

	if (isInEmptyState && props.children) {
		return <>{props.children}</>;
	}

	return (
		computedViewType === "List"
			? <DataViewList {...props as DataViewBaseProps<T, U> & DataViewDataProps<T> & DataViewListProps<T, U>} />
			: <DataViewGallery {...props as DataViewBaseProps<T, U> & DataViewDataProps<T> & DataViewGalleryProps<T, U>} />
	);
};
